import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { TourProvider } from './contexts/TourContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ProtectedSuperAdminRoute from './components/auth/ProtectedSuperAdminRoute';
import Layout from './components/layout/Layout';
import SuperAdminLayout from './components/layout/SuperAdminLayout';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy load all pages for better security and performance
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const EngagementPage = lazy(() => import('./pages/engagements/EngagementPage'));
const SubProjectsPage = lazy(() => import('./pages/engagements/SubProjectsPage'));
const FindingsPage = lazy(() => import('./pages/findings/FindingsPage'));
const SubProjectFindingsPage = lazy(() => import('./pages/engagements/SubProjectFindingsPage'));
// Old template pages removed - replaced with Word template system
// const TemplatesPage = lazy(() => import('./pages/templates/TemplatesPage'));
// const TemplateCreationPage = lazy(() => import('./pages/templates/TemplateCreationPage'));
const ReportLibraryPage = lazy(() => import('./pages/reports/ReportLibraryPage'));
const WordTemplatesPage = lazy(() => import('./pages/reports/WordTemplatesPage'));
const AdminPage = lazy(() => import('./pages/admin/AdminPage'));
const ProfilePage = lazy(() => import('./pages/user/ProfilePage'));
const NotFoundPage = lazy(() => import('./pages/misc/NotFoundPage'));
const FindingDetailPage = lazy(() => import('./pages/findings/FindingDetailPage'));
const MethodologyPage = lazy(() => import('./pages/methodology/MethodologyPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const TenantsPage = lazy(() => import('./pages/admin/TenantsPage'));
const TenantDetailPage = lazy(() => import('./pages/admin/TenantDetailPage'));
const UserManagementPage = lazy(() => import('./pages/user/UserManagementPage'));
const SystemSettingsPage = lazy(() => import('./pages/admin/SystemSettingsPage'));
const SuperAdminDashboardPage = lazy(() => import('./pages/dashboard/SuperAdminDashboardPage'));

// Super Admin Pages (kept in root for now)
const SuperAdminLoginPage = lazy(() => import('./pages/SuperAdminLoginPage'));
const SuperAdminProfilePage = lazy(() => import('./pages/SuperAdminProfilePage'));
const SuperAdminProductUpdatesPage = lazy(() => import('./pages/SuperAdminProductUpdatesPage'));
const SuperAdminSubscriptionsPage = lazy(() => import('./pages/SuperAdminSubscriptionsPage'));

// Temporarily disabled page imports
// const VulnerabilityIntelligencePage = lazy(() => import('./pages/VulnerabilityIntelligencePage'));
// const AdvancedAnalysisPage = lazy(() => import('./pages/AdvancedAnalysisPage'));
// const ContinuousMonitoringPage = lazy(() => import('./pages/ContinuousMonitoringPage'));

// Import TiptapEditorDemo component
const TiptapEditorDemo = lazy(() => import('./components/TiptapEditorDemo'));

// Import ReportPortalToolkitDemo component
const ReportPortalToolkitDemo = lazy(() => import('./pages/StatusKitDemo'));

// Import ToolkitComponentsDemo component
const ToolkitComponentsDemo = lazy(() => import('./pages/ToolkitComponentsDemo'));

// Loading component for Suspense fallback
const PageLoader: React.FC = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    minHeight="100vh"
    sx={{ backgroundColor: 'background.default' }}
  >
    <CircularProgress size={60} />
  </Box>
);

const AppContent: React.FC = () => {
  const { theme } = useTheme();
  
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <WebSocketProvider>
            <TourProvider>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginPage />} />
                  
                  {/* Super Admin routes */}
                  <Route path="/super-admin/login" element={<SuperAdminLoginPage />} />
                  
                  {/* Protected Super Admin routes */}
                  <Route
                    path="/super-admin/dashboard"
                    element={
                      <ProtectedSuperAdminRoute>
                        <SuperAdminLayout>
                          <SuperAdminDashboardPage />
                        </SuperAdminLayout>
                      </ProtectedSuperAdminRoute>
                    }
                  />
                  <Route
                    path="/super-admin/tenants"
                    element={
                      <ProtectedSuperAdminRoute>
                        <SuperAdminLayout>
                          <TenantsPage />
                        </SuperAdminLayout>
                      </ProtectedSuperAdminRoute>
                    }
                  />
                  <Route
                    path="/super-admin/tenants/:tenantId"
                    element={
                      <ProtectedSuperAdminRoute>
                        <SuperAdminLayout>
                          <TenantDetailPage />
                        </SuperAdminLayout>
                      </ProtectedSuperAdminRoute>
                    }
                  />
                  <Route
                    path="/super-admin/users"
                    element={
                      <ProtectedSuperAdminRoute>
                        <SuperAdminLayout>
                          <UserManagementPage />
                        </SuperAdminLayout>
                      </ProtectedSuperAdminRoute>
                    }
                  />
                  <Route
                    path="/super-admin/settings"
                    element={
                      <ProtectedSuperAdminRoute>
                        <SuperAdminLayout>
                          <SystemSettingsPage />
                        </SuperAdminLayout>
                      </ProtectedSuperAdminRoute>
                    }
                  />
                  <Route
                    path="/super-admin/product-updates"
                    element={
                      <ProtectedSuperAdminRoute>
                        <SuperAdminLayout>
                          <SuperAdminProductUpdatesPage />
                        </SuperAdminLayout>
                      </ProtectedSuperAdminRoute>
                    }
                  />
                  <Route
                    path="/super-admin/subscriptions"
                    element={
                      <ProtectedSuperAdminRoute>
                        <SuperAdminLayout>
                          <SuperAdminSubscriptionsPage />
                        </SuperAdminLayout>
                      </ProtectedSuperAdminRoute>
                    }
                  />
                  <Route
                    path="/super-admin/profile"
                    element={
                      <ProtectedSuperAdminRoute>
                        <SuperAdminLayout>
                          <SuperAdminProfilePage />
                        </SuperAdminLayout>
                      </ProtectedSuperAdminRoute>
                    }
                  />
                  
                  {/* Protected routes */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <DashboardPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/engagements"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <EngagementPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/subprojects"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <SubProjectsPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/findings"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <FindingsPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/findings/:findingId"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <FindingDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/subprojects/:subProjectId/findings"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <SubProjectFindingsPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  {/* Temporarily disabled pages */}
                  {/* <Route
                    path="/vulnerability-intelligence"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <VulnerabilityIntelligencePage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/advanced-analysis"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AdvancedAnalysisPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/continuous-monitoring"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ContinuousMonitoringPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  /> */}
                  {/* New Word Template Management */}
                  <Route
                    path="/word-templates"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <WordTemplatesPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/report-library"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ReportLibraryPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/methodology"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <MethodologyPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/calendar"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <CalendarPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  {/* Temporarily disabled scoreboard */}
                  {/* <Route
                    path="/scoreboard"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ScoreboardPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  /> */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute requiredRole="Admin">
                        <Layout>
                          <AdminPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ProfilePage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* TiptapEditor Demo Route */}
                  <Route
                    path="/tiptap-demo"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <TiptapEditorDemo />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* ReportPortal Toolkit Demo Route */}
                  <Route
                    path="/toolkit-demo"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ReportPortalToolkitDemo />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* ReportPortal Toolkit Components Demo Route */}
                  <Route
                    path="/toolkit-components-demo"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ToolkitComponentsDemo />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Root redirect - redirect to login if not authenticated, dashboard if authenticated */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Navigate to="/dashboard" replace />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* 404 page for all non-existing routes */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </TourProvider>
          </WebSocketProvider>
        </Router>
      </AuthProvider>
    </MuiThemeProvider>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App; 